import { useEffect, useRef } from 'react';
import useRealtimeSocket from './useRealtimeSocket';
import { useAuth } from '../context/AuthContext';
import { useThreadMutes } from './useThreadFeatures';
import useUserMutes from './useUserMutes';

/**
 * Chat notification sound — short chime on every incoming chat message.
 *
 * Layered on top of the existing Rule-20 / Rule-71 realtime pipeline. Mounted
 * once at the AppShell level so the sound fires on EVERY page (Dashboard,
 * Profile, anywhere), not just on the chat page itself.
 *
 * The WS pipeline emits TWO related events for each new chat message:
 *
 *   1. `CHAT_MESSAGE`      — room-scoped fan-out via
 *                            `RealtimeBroadcaster::toRoom('chat', $threadId, ...)`.
 *                            Only delivered to clients that have JOIN_ROOM'd
 *                            `chat:{threadId}` (i.e. the user is currently on
 *                            the chat page with that thread open).
 *                            Payload: `{ type, thread_id, message:{sender_user_id,id,...} }`
 *
 *   2. `CHAT_NOTIFICATION` — per-user fan-out via
 *                            `RealtimeBroadcaster::toUser($recipientId, ...)`.
 *                            Delivered to every authenticated client of the
 *                            recipient REGARDLESS of which page they're on
 *                            (the recipient list excludes the sender, so this
 *                            never echoes back to the author).
 *                            Payload: `{ type, room, thread_id, sender_id, preview, notification }`
 *
 * **The chime listens for `CHAT_NOTIFICATION` as its primary trigger** —
 * that's the only path that fires when the user is on Dashboard / Profile /
 * any non-chat page. `CHAT_MESSAGE` is wired as a defensive secondary so the
 * sound still works even if a future backend stops dual-publishing.
 * Per-message dedupe (`Set<threadId:messageId>`) prevents double-chime when
 * both events arrive for the same message (user is on the active thread).
 *
 * Silencing rules (any one suppresses):
 *   - The message is FROM the auth user themselves (loopback — backend
 *     already excludes the sender from `CHAT_NOTIFICATION`, but we re-check
 *     defensively in case the secondary `CHAT_MESSAGE` path triggers).
 *   - The thread is in `useThreadMutes().mutedSet` — silenced.
 *   - The sender is in `useUserMutes().mutedSet` — silenced.
 *
 * Per-thread 1.5s burst throttle prevents rapid-fire bursts (someone typing
 * 10 short messages in quick succession) from machine-gunning the speaker.
 *
 * Browser auto-play policy: HTMLAudioElement.play() returns a rejected
 * promise when the tab has not yet received a user gesture. We swallow that
 * rejection silently — the visual notification (unread badge, NotificationBell)
 * covers the always-fires path, and the first user gesture (click/tap)
 * unblocks audio for the rest of the session.
 */
export default function useChatNotificationSound({ enabled = true } = {}) {
  const { subscribeAll } = useRealtimeSocket();
  const { user } = useAuth();
  const { mutedSet: threadMutedSet } = useThreadMutes();
  const { mutedSet: userMutedSet }   = useUserMutes();

  // Refs hold the latest values without resubscribing the WS listener on
  // every render (resubscribing tears down + re-creates the channel handler
  // every keystroke / route change, which would lose buffered events).
  const userIdRef         = useRef(0);
  const threadMutedRef    = useRef(threadMutedSet);
  const userMutedRef      = useRef(userMutedSet);
  const lastFiredAtRef    = useRef(new Map());      // threadId → timestamp ms
  const dedupeKeysRef     = useRef(new Set());      // "threadId:messageId" recently played

  userIdRef.current      = Number(user?.id ?? 0);
  threadMutedRef.current = threadMutedSet;
  userMutedRef.current   = userMutedSet;

  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof window === 'undefined') return undefined;
    if (!subscribeAll) return undefined;

    const url = (process.env.REACT_APP_CHAT_NOTIFICATION_SOUND_URL || '').trim();
    const player = url ? createMp3Player(url) : createSynthPlayer();

    // Normalise both event shapes into a single { threadId, senderId, dedupeKey }
    // tuple. Returns null when the event is not chime-worthy.
    const extractTrigger = (event) => {
      if (!event) return null;

      // Primary path — per-user fan-out (fires on every page).
      if (event.type === 'CHAT_NOTIFICATION') {
        const threadId = Number(event.thread_id ?? 0);
        const senderId = Number(event.sender_id ?? 0);
        // CHAT_NOTIFICATION has no message_id in the canonical payload, so
        // dedupe by (threadId, notificationId|approxTimestamp) — the goal
        // is purely to prevent double-chime with the room-scoped event for
        // the same message.
        const notifId = Number(event.notification?.id ?? 0);
        const dedupeKey = notifId
          ? `n:${threadId}:${notifId}`
          : `n:${threadId}:${Math.floor(Date.now() / 1000)}`;
        return { threadId, senderId, dedupeKey };
      }

      // Defensive secondary path — room-scoped event (only fires when the
      // user is JOIN_ROOM'd to this chat thread).
      if (event.type === 'CHAT_MESSAGE') {
        const message  = event.message ?? {};
        const threadId = Number(message.thread_id ?? event.thread_id ?? 0);
        const senderId = Number(message.sender_user_id ?? message.user_id ?? 0);
        const messageId = Number(message.id ?? 0);
        const dedupeKey = messageId
          ? `m:${threadId}:${messageId}`
          : `m:${threadId}:${Math.floor(Date.now() / 1000)}`;
        return { threadId, senderId, dedupeKey };
      }

      return null;
    };

    const unsubscribe = subscribeAll((event) => {
      const trigger = extractTrigger(event);
      if (!trigger) return;

      const { threadId, senderId, dedupeKey } = trigger;
      const viewerId = Number(userIdRef.current ?? 0);

      if (!threadId) return;
      if (senderId && viewerId && senderId === viewerId) return;        // self
      if (threadMutedRef.current?.has?.(threadId)) return;              // thread muted
      if (senderId && userMutedRef.current?.has?.(senderId)) return;    // sender muted

      // Dedupe same logical message arriving via both CHAT_NOTIFICATION
      // AND CHAT_MESSAGE on the active-thread page.
      if (dedupeKeysRef.current.has(dedupeKey)) return;
      dedupeKeysRef.current.add(dedupeKey);
      // Bound the dedupe set so a long session doesn't leak memory.
      if (dedupeKeysRef.current.size > 200) {
        // Drop the oldest by rebuilding from the most-recent slice.
        const arr = Array.from(dedupeKeysRef.current);
        dedupeKeysRef.current = new Set(arr.slice(-100));
      }

      const now = Date.now();
      const lastAt = lastFiredAtRef.current.get(threadId) ?? 0;
      if (now - lastAt < THROTTLE_MS) return;                           // burst throttle
      lastFiredAtRef.current.set(threadId, now);

      player.play();
    });

    return () => {
      try { unsubscribe?.(); } catch (_) { /* idem */ }
      try { player.dispose(); } catch (_) { /* idem */ }
    };
  }, [enabled, subscribeAll]);
}

const THROTTLE_MS = 1500;

function createMp3Player(url) {
  // Pre-construct the Audio element ONCE so the file is fetched + decoded
  // up front; subsequent plays reuse the same buffer instead of going to
  // the network each time. Setting currentTime=0 before each play() lets
  // rapid plays restart cleanly mid-playback.
  const audio = new Audio(url);
  audio.preload = 'auto';
  audio.volume = 0.6;

  return {
    play: () => {
      try {
        audio.currentTime = 0;
        const p = audio.play();
        if (p && typeof p.catch === 'function') {
          p.catch(() => { /* autoplay-policy block — silent fail intended */ });
        }
      } catch (_) {
        /* element raced with disposal — silent fail intended */
      }
    },
    dispose: () => {
      try { audio.pause(); } catch (_) { /* idem */ }
      try { audio.src = ''; audio.load(); } catch (_) { /* idem */ }
    },
  };
}

function createSynthPlayer() {
  // Lazily build the AudioContext on the FIRST play so we don't hold an
  // open context for tabs that never receive a message. Same auto-play
  // policy applies — first attempt may be blocked, post-gesture it works.
  let ctx = null;
  const ensureCtx = () => {
    if (ctx) return ctx;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return null;
    ctx = new AudioCtx();
    return ctx;
  };

  return {
    play: () => {
      try {
        const audioCtx = ensureCtx();
        if (!audioCtx) return;
        if (audioCtx.state === 'suspended') {
          audioCtx.resume().catch(() => { /* policy — silent */ });
        }
        const now = audioCtx.currentTime;
        const osc = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        osc.type = 'sine';
        // 880Hz keeps the chat chime distinct from the ringtone's 440/480Hz
        // brrring so a user simultaneously receiving a call + a chat message
        // can tell them apart by ear alone.
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(0.18, now + 0.02);
        gain.gain.setValueAtTime(0.18, now + 0.13);
        gain.gain.linearRampToValueAtTime(0, now + 0.18);
        osc.connect(gain).connect(audioCtx.destination);
        osc.start(now);
        osc.stop(now + 0.22);
      } catch (_) {
        /* synth raced with disposal — silent */
      }
    },
    dispose: () => {
      try { ctx?.close(); } catch (_) { /* idem */ }
      ctx = null;
    },
  };
}
